import { Autocomplete, Chip, IconButton, Skeleton, SvgIcon, TextField, Typography } from "@mui/material";
import { Box, minWidth, Stack, width } from "@mui/system";
import { DataGridPro, GridColDef, GridColumnVisibilityModel, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo } from "react";
import { useRef, useState } from "react";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import Iconify from "src/components/iconify";
import { useGetBrandsQuery, useGetCountriesQuery } from "src/context/api/brandPulse/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { formatCurrency } from "src/utils/format-currency";
import { formatNumber, fPercent } from "src/utils/format-number";
import RegionSelector from "./region-selector";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { t } from "i18next";
import ModalTrackingBrand from "./modalTrackingBrand";
import { setFilterCountry } from "src/context/reducers/filter-settings/filterSlice";
import PresenceRateChartNew from "src/components/visibility/components/PresenceRateChartNew";
import { useNavigate } from "react-router";


export default function Brands() {
  const apiRef: any = useRef();
  const workspace: any = useCurrentWorkspace();
  const workspaceId: any = workspace?.id;
  const lang = localStorage.getItem('i18nextLng') || 'en';
  const dispatch = useDispatch();
  const filterStore = useSelector((state: RootState) => state.filters);
  const navigate = useNavigate();

  const [modalTrackingBrand, setModalTrackingBrand]: any = useState<{
    open: boolean;
    domain: string | null;
  }>({ open: false, domain: null });

  const dateRangeOptions = [
    { label: 'Last Month', value: 'last_month' },
    { label: 'Last 3 Months', value: 'last_3_months' },
    { label: 'Last 6 Months', value: 'last_6_months' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Last Year', value: 'last_year' },
  ];

  const comparisonPeriodOptions = [
    { label: 'Prev. Period', value: 'prev_period' },
    { label: 'Prev. Year', value: 'prev_year' },
  ];

  const [selectedDateRange, setSelectedDateRange] = useState(dateRangeOptions[1]);
  const [selectedComparisonPeriod, setSelectedComparisonPeriod]: any = useState(comparisonPeriodOptions[0]);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const toolbarRef: any = useRef();

  const CustomToolbar = (props: any) => {
    return (
      <Stack
        ref={toolbarRef}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={0}
        px={2.5}
        py={1.25}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: 'white!important',
          height: 40,
          minHeight: 40,
          px: "20px",
          "& .MuiButton-root": {
            height: 32,
            minHeight: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textTransform: "none",
          },

          "& .MuiAutocomplete-root": {
            display: "flex",
            alignItems: "center",
          },

          "& .MuiInputBase-root": {
            height: 32,
            minHeight: 32,
            display: "flex",
            alignItems: "center",
            paddingTop: "0 !important",
            paddingBottom: "0 !important",
          },

          "& .MuiAutocomplete-input": {
            padding: "0 !important",
          },

          "& .MuiChip-root": {
            height: 24,
          },
        }}
      >

        <Stack direction="row" spacing={1}>
          <CustomTooltip title={"Show More KPIs"} arrow placement="top">
            <IconButton
              onClick={() => apiRef.current?.showPreferences("columns")}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: "#919EAB",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Iconify
                icon="mingcute:calendar-week-line"
                width={18}
                height={18}
              />
            </IconButton>
          </CustomTooltip>

          <CustomTooltip title={"Apply Filters"} arrow placement="top">
            <IconButton
              onClick={() => apiRef.current?.showFilterPanel()}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: "#919EAB",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Iconify
                icon="ic:round-filter-list"
                width={18}
                height={18}
              />
            </IconButton>
          </CustomTooltip>

          <CustomTooltip title={"Download Table"} arrow placement="top">
            <IconButton
              onClick={() => apiRef.current?.exportDataAsCsv({
                fileName: 'tracked_competitors',
                utf8WithBom: true
              })}
              sx={{
                width: 26,
                height: 26,
                padding: 0,
                color: "#919EAB",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M14 10V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V10M4.66667 6.66667L8 10L11.3333 6.66667M8 10V2" stroke="#919EAB" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </IconButton>
          </CustomTooltip>
        </Stack>

        <Stack direction="row" alignItems="center" px={1}>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              disablePortal
              options={dateRangeOptions}
              size="small"
              disableClearable
              onChange={(event, newValue) => setSelectedDateRange(newValue!)}
              getOptionLabel={(option) => option.label}
              sx={{ width: 150 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedDateRange && (
                          <Chip
                            label={selectedDateRange.label}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(145, 158, 171, 0.5)"
                              }
                            }}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              disablePortal
              options={comparisonPeriodOptions}
              disableClearable
              size="small"
              onChange={(event, newValue) => setSelectedComparisonPeriod(newValue!)}
              getOptionLabel={(option) => option.label}
              sx={{ width: 160 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedComparisonPeriod && (
                          <Chip
                            label={selectedComparisonPeriod.label}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(145, 158, 171, 0.32)',
                              color: 'rgba(51, 60, 71, 1)',
                              mr: 1,
                              "&:hover": {
                                backgroundColor: "rgba(145, 158, 171, 0.5)"
                              }
                            }}
                          />
                        )}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Stack>


        </Stack>
      </Stack>
    )
  }

  interface PercentageChangeProps {
    value: number | null;
    inverse?: boolean;
    rounded?: number;
    format?: string;
  }

  type FormatType = "percentage" | "pp";

  const PercentageChange: React.FC<PercentageChangeProps & { format?: FormatType }> = ({
    value,
    inverse = false,
    rounded = 0,
    format = "percentage",
  }) => {
    if (value === null || value === undefined) return null;

    const suffix = format === "pp" ? "pp" : "%";

    const isZeroAfterRound = Number(value.toFixed(rounded)) === 0;

    const isDash = value === 0 || isZeroAfterRound;

    const color =
      isDash
        ? "rgba(145, 158, 171, 1)"
        : value > 0
          ? (inverse ? "rgba(220, 24, 24, 1)" : "rgba(0, 162, 91, 1)")
          : value < 0
            ? (inverse ? "rgba(0, 162, 91, 1)" : "rgba(220, 24, 24, 1)")
            : "rgba(145, 158, 171, 1)";

    const style: React.CSSProperties = {
      color,
      marginLeft: "8px",
      textAlign: "right",
      fontVariantNumeric: "lining-nums tabular-nums",
      fontFamily: 'var(--font-family-Font-1, "Public Sans")',
      fontSize: "14px",
      lineHeight: "22px",
    };

    if (value > 1000) {
      return <span style={style}>{`>999${suffix}`}</span>;
    }

    if (value < -1000) {
      return <span style={style}>{`<-999${suffix}`}</span>;
    }

    const roundedValue = Number(value.toFixed(rounded));

    if (roundedValue === 0) {
      return <span style={style}>-</span>;
    }

    const sign = roundedValue > 0 ? "+" : "";

    return (
      <span style={style}>
        {sign}
        {roundedValue}
        {suffix}
      </span>
    );
  };

  const { data: brandsData, isLoading: isBrandsLoading, isFetching } = useGetBrandsQuery(
    {
      workspace_id: workspaceId,
      country: filterStore?.countries[0],
      date: selectedDateRange?.value,
      compareDate: selectedComparisonPeriod?.value
    },
    {
      skip: !workspaceId || !filterStore?.countries[0],
      refetchOnMountOrArgChange: true,
    }
  );

  const clientDomain = workspace?.url || "";
  const friendlyDomain = workspace?.friendlyDomains || [];

  const { pinnedRows, rows } = useMemo(() => {
    if (!brandsData?.data) return { pinnedRows: [], rows: [] };

    const dataCopy = [...brandsData.data];

    const readyRows = dataCopy
      .filter(r => r.data_status === true)
      .sort((a, b) => (b.perc_clicks || 0) - (a.perc_clicks || 0));

    const notReadyRows = dataCopy.filter(r => r.data_status === false);

    const finalRows = [
      ...notReadyRows,
      ...readyRows
    ].map((brand, index) => ({
      id: index + 1,
      rank: brand?.rank,
      domains: brand?.domain_name,


      organic_presence: brand?.organic_presence,
      solo_bidder: brand?.solo_bidder,
      top_ad_position: brand?.top_ad_position,
      compet_only: brand?.compet_only,

      organic_presence_compare: brand?.compare?.organic_presence,
      solo_bidder_compare: brand?.compare?.solo_bidder,
      top_ad_position_compare: brand?.compare?.top_ad_position,
      compet_only_compare: brand?.compare?.compet_only,


      data_status: brand?.data_status,

      organic_volume: brand?.organic_volume,
      paid_volume: brand?.paid_volume,
      organic: brand?.organic,
      paid: brand?.paid,

      organic_volume_compare: brand?.compare?.organic_volume,
      paid_volume_compare: brand?.compare?.paid_volume,
      organic_compare: brand?.compare?.organic,
      paid_compare: brand?.compare?.paid,

      monthly_searches: brand?.monthly_searches,
      monthly_search_volume_compare: brand?.compare?.monthly_searches || 0,

      monthly_spend: brand?.monthly_spend,
      monthly_spend_compare: brand?.compare?.monthly_spend,

      visibility_score: brand?.visibility_score,
      visibility_score_compare: brand?.compare?.visibility_score,

      perc_clicks: brand?.perc_clicks || 0,
    }));

    const isClientRow = (row: any) =>
      row.domains === clientDomain || friendlyDomain?.includes(row.domains);

    return {
      pinnedRows: finalRows.filter(isClientRow),
      rows: finalRows.filter(r => !isClientRow(r)),
    };
  }, [brandsData, workspace?.url, workspace?.friendlyDomains, filterStore?.countries[0]]);

  const allRows = [...pinnedRows, ...rows];
  const hasRows = allRows.length > 0;

  const { data: countriesData, isLoading: isLoadingCountries }: any = useGetCountriesQuery(
    {
      workspace_id: workspace?.bqTable,
    }
  );

  const isLoading = isLoadingCountries || isBrandsLoading || isFetching || !brandsData;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    organic_vs_paid_compare: false,
    visibility_rate_compare: false,
    monthly_search_volume_compare: false,
    visibility_score_diff: false,
    monthly_spend_compare: false,
  });

  const handleChangeColumnVisibilityModel = useCallback((newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
  }, []);

  useEffect(() => {
    if (countriesData?.length) {
      const countryWithMaxShare = countriesData.reduce((max: any, current: any) =>
        current.share_of_brand_searches > max.share_of_brand_searches ? current : max
      );

      dispatch(setFilterCountry([countryWithMaxShare.country_name]));
    }
  }, [countriesData]);


  const maxMonthlySearches = useMemo(() => {
    const data = brandsData?.data || [];

    return Math.max(
      ...data.map((row: any) => row.monthly_searches || 0)
    );
  }, [brandsData?.data]);

  const maxMonthlySpend = useMemo(() => {
    const data = brandsData?.data || [];

    return Math.max(
      ...data.map((row: any) => Number(row.monthly_spend) || 0)
    );
  }, [brandsData?.data]);

  const columns: GridColDef[] = [
    {
      field: 'rank',
      headerAlign: "left",
      headerName: t('dashboard.global.brandPulse.rank'),
      disableColumnMenu: true,
      renderHeader: () => (
        <CustomTooltip title={t('dashboard.global.brandPulse.definitions.rank')} placement="top">
          <Box sx={{ cursor: "help" }}>
            <Typography color={"#637381"} fontSize={"14px"} fontWeight={600}>{t('dashboard.global.brandPulse.rank')}</Typography>
          </Box>
        </CustomTooltip>
      ),
      type: "number",
      width: 60,
      renderCell: (params: any) => (
        <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
          {params.row.data_status
            ? <Typography fontSize={"14px"} color={"rgba(47, 56, 67, 1)"}>
              #{params.row.rank}
            </Typography>
            : <CustomTooltip title="Tracking in progress, it might take 48h hours to complete" placement="top">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                  width: 40,
                  height: 40,
                  transition: "all 0.2s ease",
                  position: "relative",
                  "&:hover": {
                    cursor: "pointer",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: -2,
                      left: -4,
                      right: -4,
                      bottom: -2,
                      borderRadius: "6px",
                      background: "rgba(118, 53, 220, 0.08)",
                    },
                  },
                }}
              >
                <Iconify icon="svg-spinners:bars-rotate-fade" width={24} height={24} />
              </Box>
            </CustomTooltip>
          }
        </Stack>
      ),
    },
    {
      field: 'domains',
      headerAlign: "left",
      flex: 1,
      headerName: t('dashboard.global.brandPulse.domains'),
      minWidth: 250,
      maxWidth: 350,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box>
          <Typography color={"#637381"} fontSize={"14px"} fontWeight={600}>{t('dashboard.global.brandPulse.domains')}</Typography>
        </Box>
      ),
      renderCell: (params: any) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 1, height: 1, width: "100%" }}
        >
          {/* LEFT SIDE */}
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>

            {/* FAVICON */}
            <Box
              sx={{
                width: 24,
                height: 24,
                border: "1px solid rgba(129, 129, 129, 0.2)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={`https://www.google.com/s2/favicons?domain=${params.row.domains}&sz=32`}
                alt="favicon"
                style={{ width: 16, height: 16 }}
              />
            </Box>

            {/* DOMAIN (TRUNCATED) */}
            <Typography
              fontSize="14px"
              color="rgba(47, 56, 67, 1)"
              noWrap
              sx={{
                maxWidth: 120, // ajuste selon ton UI
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.domains}
            </Typography>
          </Box>

          {/* RIGHT SIDE */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                background:
                  "linear-gradient(90deg, rgba(249, 249, 250, 0.00) 0%, #F9F9FA 55.09%)",
                color: "rgba(0, 108, 156, 0.85)",
                padding: "2px 4px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                "&:hover": {
                  textDecoration: "underline"
                }
              }}
              onClick={() =>
                params.row.data_status &&
                setModalTrackingBrand({
                  open: true,
                  domain: params?.row?.domains,
                })
              }
            >
              See evolution
              <SvgIcon
                component={() => (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="8"
                    height="8"
                    viewBox="0 0 8 8"
                    fill="none"
                  >
                    <path
                      d="M0.599609 7.26628L7.26628 0.599609M7.26628 0.599609H0.599609M7.26628 0.599609V7.26628"
                      stroke="#006C9C"
                      strokeOpacity="0.85"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              />
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      field: 'monthly_searches',
      disableColumnMenu: true,
      flex: 1,
      headerAlign: "left",
      headerName: t('dashboard.global.brandPulse.monthly_searches'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.monthly_searches')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.monthly_searches')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      type: 'number',
      align: 'right',
      cellClassName: "no-padding-cell",
      renderCell: (params: any) => {
        const value = params.row.monthly_searches || 0;

        const percent = maxMonthlySearches
          ? (value / maxMonthlySearches) * 100
          : 0;

        const compare = params.row?.monthly_search_volume_compare || 0;

        const diffValue = value - compare;
        const diffPercent = compare !== 0 ? (diffValue / compare) * 100 : 0;

        return (
          params.row.data_status && (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                pr: 1,
                overflow: "hidden",
              }}
            >
              {/* LEFT: BAR ZONE */}
              <Box
                sx={{
                  position: "relative",
                  width: "70%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexShrink: 0,
                    zIndex: 9999,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#2F3843",
                      fontFamily: '"Public Sans", sans-serif',
                      fontSize: "14px",
                      fontWeight: 400,
                      lineHeight: "22px",
                      fontVariantNumeric: "tabular-nums lining-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatNumber(value)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${percent}%`,
                    backgroundColor: "#FDF4F2",
                    transition: "width 0.3s ease",
                    borderTopRightRadius: "999px",
                    borderBottomRightRadius: "999px",
                  }}
                />
              </Box>

              {/* RIGHT: NUMBERS ALWAYS RIGHT */}
              <Box
                sx={{
                  width: "30%",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <PercentageChange value={diffPercent} />
              </Box>
            </Box>
          )
        );
      }
    },
    {
      field: 'organic_vs_paid',
      disableColumnMenu: true,
      headerAlign: "left",
      width: 300,
      filterable: false,
      headerName: t('dashboard.global.brandPulse.organic_vs_paid'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.organic_vs_paid')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.organic_vs_paid')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      align: 'center',
      renderCell: (params: any) => {
        const tooltipContent = (
          <Box sx={{ width: '250px' }}>
            <Box className="visibility-chart-tooltip-header" sx={{ mb: 1, fontWeight: 'bold' }}>
              <Typography className="visibility-chart-tooltip-title" variant="body2">
                {t('dashboard.global.brandPulse.organic_paid')}
              </Typography>
            </Box>

            <Box className="visibility-chart-tooltip-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <div className="visibility-chart-tooltip-indicator" style={{ backgroundColor: '#18A155' }}></div>

              <Typography className="visibility-chart-tooltip-label" variant="body2">
                {t('dashboard.global.brandPulse.organic')}
              </Typography>
              <Typography sx={{ color: 'rgba(51, 60, 71, 1)' }} variant="body2">
                {formatNumber(params?.row?.organic_volume?.toFixed(0))}
              </Typography>
            </Box>

            <Box className="visibility-chart-tooltip-row" sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <div className="visibility-chart-tooltip-indicator" style={{ backgroundColor: '#C09910' }}></div>

              <Typography className="visibility-chart-tooltip-label" variant="body2">
                {t('dashboard.global.brandPulse.paid')}
              </Typography>
              <Typography sx={{ color: 'rgba(51, 60, 71, 1)' }} variant="body2">
                {formatNumber(params?.row?.paid_volume?.toFixed(0))}
              </Typography>
            </Box>
          </Box>
        );

        return (
          <>
            {params.row.data_status && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: "100%", height: "100%" }}
              >
                {/* LEFT % (fixed width) */}
                <Typography
                  variant="caption"
                  sx={{
                    width: 40,
                    textAlign: "right",
                    fontSize: "0.875rem",
                    color: "rgba(47, 56, 67, 1)",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {params?.row?.organic?.toFixed(0)}%
                </Typography>

                {/* BAR */}
                <CustomTooltip noPadding title={tooltipContent} placement="top">
                  <Box
                    sx={{
                      flexGrow: 1,
                      height: 12,
                      display: "flex",
                      borderRadius: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${params?.row?.organic?.toFixed(0)}%`,
                        backgroundColor: "#18A155",
                      }}
                    />

                    <Box
                      sx={{
                        width: `${params?.row?.paid?.toFixed(0)}%`,
                        backgroundColor: "#DAA335",
                        ml: "2px",
                      }}
                    />
                  </Box>
                </CustomTooltip>

                {/* RIGHT % (fixed width + aligned mirror) */}
                <Typography
                  variant="caption"
                  sx={{
                    width: 40,
                    textAlign: "right",
                    fontSize: "0.875rem",
                    color: "rgba(47, 56, 67, 1)",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {params?.row?.paid?.toFixed(0)}%
                </Typography>
              </Stack>
            )}
          </>
        );
      }
    },
    {
      field: 'monthly_spend',
      headerAlign: "left",
      flex: 1,
      disableColumnMenu: true,
      headerName: t('dashboard.global.brandPulse.monthly_spend'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.monthly_spend')}
          placement="top"
        >
          <Box sx={{ cursor: "help" }}>
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.monthly_spend')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      type: "number",
      align: "right",
      cellClassName: "no-padding-cell",
      renderCell: (params: any) => {
        if (!params.row.data_status) return null;

        const current = Number(params.row.monthly_spend) || 0;
        const previous = Number(params.row.monthly_spend_compare) || 0;

        const diffPercent =
          previous !== 0
            ? ((current - previous) / previous) * 100
            : 0;

        const percent = maxMonthlySpend
          ? (current / maxMonthlySpend) * 100
          : 0;

        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              pr: 1,
              overflow: "hidden",
            }}
          >
            {/* LEFT: BAR ZONE */}
            <Box
              sx={{
                position: "relative",
                width: "70%",
                height: "100%",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: 70,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexShrink: 0,
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    color: "#2F3843",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "22px",
                    fontVariantNumeric: "tabular-nums lining-nums",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatCurrency(current, workspace?.currency, lang, true, 1)}
                </Typography>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${percent}%`,
                  backgroundColor: "#FAF1E1",
                  transition: "width 0.3s ease",
                  borderTopRightRadius: "999px",
                  borderBottomRightRadius: "999px",
                }}
              />
            </Box>

            {/* RIGHT: % CHANGE */}
            <Box
              sx={{
                width: "30%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
                zIndex: 1,
                whiteSpace: "nowrap",
              }}
            >
              <PercentageChange value={diffPercent} />
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'visibility_score',
      flex: 1,
      type: "number",
      headerAlign: "left",
      headerName: t('dashboard.global.brandPulse.visibility_rate'),
      renderHeader: () => (
        <CustomTooltip
          title={t('dashboard.global.brandPulse.definitions.visibility_rate')}
          placement="top"
        >
          <Box
            sx={{
              cursor: "help",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.2,
              }}
              color="#637381"
              fontSize="14px"
              fontWeight={600}
            >
              {t('dashboard.global.brandPulse.visibility_rate')}
            </Typography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const current = Number(params.row.visibility_score);
        const previous = Number(params.row.visibility_score_compare);

        const ppDiff =
          Number.isFinite(previous)
            ? current - previous
            : null;

        const data = {
          organicOnly: Number(params?.row?.organic_presence?.toFixed(0)) || 0,
          soloBidder: Number(params?.row?.solo_bidder?.toFixed(0)) || 0,
          topAdPosition: Number(params?.row?.top_ad_position?.toFixed(0)) || 0,
          competitorOnly: Number(params?.row?.compet_only?.toFixed(0)) || 0,
        };

        return (
          <>
            {params.row.data_status && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between", // 👈 clé
                  width: "100%",
                  height: "100%",
                  gap: 1,
                }}
              >
                {/* LEFT: value */}
                <Typography
                  variant="body2"
                  sx={{
                    width: 40,
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {fPercent(current)}
                </Typography>

                {/* MIDDLE: chart */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: 1,
                  }}
                >
                  <PresenceRateChartNew
                    data={data}
                    height={40}
                    totalVisibilityRate={current}
                  />
                </Box>

                {/* RIGHT: PP */}
                <Box
                  sx={{
                    minWidth: 60,
                    display: "flex",
                    justifyContent: "flex-end",
                    flexShrink: 0,
                  }}
                >
                  <PercentageChange
                    value={ppDiff}
                    format="pp"
                  />
                </Box>
              </Box>
            )}
          </>
        );
      }
    },
  ].filter(Boolean) as GridColDef[];

  const CustomNoRowsOverlay = () => {
    return (
      <Stack
        height="100%"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: 'white',
          color: '#637381',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'rgba(229, 218, 253, 0.40)',
              p: '12px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify color={"rgba(118, 85, 193, 1)"} icon="clarity:paste-line" width={48} height={48} />
          </Box>
        </Box>

        <Typography fontSize={'16px'} color={"rgba(51, 60, 71, 1)"} variant="subtitle1" fontWeight={600} mt={"12px"}>
          No selected brands
        </Typography>
        <Box mt={"12px"} display={"flex"} flexDirection={"row"}>
          <Typography color={"rgba(99, 115, 129, 1)"} fontSize={'14px'} textAlign={"center"} variant="body2">
            You haven’t Selected any competitors currently, go to <br />
            <Box
              component="span"
              sx={{
                color: "rgba(81, 25, 183, 1)",
                cursor: "pointer",
                fontSize: "16px",
                textDecoration: "underline",
                marginRight: .6, // MUI spacing scale (4px * 1)
                "&:hover": {
                  color: "rgba(81, 25, 183, 0.75)"
                }
              }}
              onClick={() => navigate("/competitors/leaderboard/competitors-selection")}
            >
              Competitors Selection
            </Box>
            tab to select few brands and track them.
          </Typography>
        </Box>
      </Stack>
    );
  };

  return (
    <>
      <Box>

        <RegionSelector data={countriesData} loading={isLoadingCountries} />

        <Box px={'24px'} mt={isLoading ? '24px' : (countriesData && countriesData?.length > 1) ? '24px' : '0px'} height={"600px"}>
          {isLoading
            ? <Skeleton width={'100%'} height={700} variant="rectangular" animation="wave" sx={{
              borderRadius: '8px',
            }} />
            : <DataGridPro
              sx={{
                ...(!hasRows && { height: "600px" }),
                border: "1px solid rgba(145, 158, 171, 0.2)",
                borderRadius: '8px',
                '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },

                "& .no-padding-cell.MuiDataGrid-cell": {
                  paddingLeft: 0,
                  paddingRight: 0,
                },
                '& .MuiDataGrid-columnHeader, .MuiDataGrid-filler': {
                  backgroundColor: 'rgba(226, 229, 233, 0.16) !important',
                  height: "53px!important",
                  color: "rgba(99, 115, 129, 1)",
                  fontWeight: 'normal!important'
                },
                '& .MuiDataGrid-cell': {
                  border: "1px solid rgba(145, 158, 171, 0.2)",
                  color: 'rgba(51, 60, 71, 1)',
                  backgroundColor: 'white',
                },
                "& .client-row .MuiDataGrid-cell": {
                  backgroundColor: "rgba(0, 143, 205, 0.06)",
                  fontWeight: "bold",
                },
                ...(rows.length === 0 && pinnedRows.length > 0 && {
                  '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell:first-of-type': {
                    borderBottomLeftRadius: '8px',
                  },
                  '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell:last-of-type': {
                    borderBottomRightRadius: '8px',
                  },
                }),
                "& .data-not-ready .MuiDataGrid-cell": {
                  backgroundColor: "rgba(118, 53, 220, 0.08)",
                  fontWeight: "bold",
                },
                width: '100%',
                '& .MuiDataGrid-cellContent': {
                  overflow: 'visible !important',
                },
                '& .highlighted-row': {
                  opacity: 0.3,
                },
                "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  color: '#919EAB!important'
                },
                "& .css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input.css-nnlark-MuiSelect-select-MuiInputBase-input": {
                  borderRadius: '6px',
                  border: "1px solid rgba(145, 158, 171, 0.20)",
                  color: "#919EAB!important"
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root": {
                  color: "#919EAB!important",
                },
                "& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled": {
                  color: "#d0d5da!important",
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: "white",
                  borderTop: "1px solid rgba(145, 158, 171, 0.2)",
                },
              }}
              getRowClassName={(params) => {
                const classes = [];
                if (params.row.domains === clientDomain) classes.push("client-row");
                if (friendlyDomain?.includes(params.row.domains)) classes.push("client-row");

                if (params.row.data_status === false) classes.push("data-not-ready");
                return classes.join(" ");
              }}
              apiRef={apiRef}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
              columns={columns}
              pagination={rows.length !== 0}
              hideFooter={rows.length === 0}
              rows={rows}
              pinnedRows={rows.length !== 0 ? { top: pinnedRows } : undefined}
              hideFooterPagination={rows.length === 0}
              hideFooterSelectedRowCount
              density="standard"
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}

              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              slots={{
                toolbar: CustomToolbar,
                noRowsOverlay: CustomNoRowsOverlay,
              }}
              slotProps={{
                noRowsOverlay: {
                  sx: {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              }}
              rowHeight={57}
              columnHeaderHeight={50}
            />
          }
        </Box>
      </Box>

      <ModalTrackingBrand
        region={filterStore?.countries[0]}
        open={modalTrackingBrand?.open}
        domain={modalTrackingBrand?.domain} onClose={() => setModalTrackingBrand({ open: false, domain: null })}
      />

    </>
  )
}